import { Injectable, signal } from '@angular/core';

export type DialogType = 'alert' | 'confirm' | 'prompt';

export interface DialogConfig {
  type: DialogType;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  placeholder?: string;
  /** Marks the dialog as destructive (red confirm button) */
  destructive?: boolean;
}

export interface DialogState extends DialogConfig {
  open: boolean;
  resolve: ((value: string | boolean | null) => void) | null;
}

@Injectable({ providedIn: 'root' })
export class DialogService {
  readonly state = signal<DialogState>({
    open: false,
    type: 'alert',
    message: '',
    resolve: null
  });

  private open(config: DialogConfig): Promise<string | boolean | null> {
    return new Promise((resolve) => {
      this.state.set({ ...config, open: true, resolve });
    });
  }

  alert(message: string, title = 'Notice'): Promise<void> {
    return this.open({ type: 'alert', message, title }).then(() => {});
  }

  confirm(
    message: string,
    title = 'Confirm',
    destructive = false,
    confirmText = 'Confirm'
  ): Promise<boolean> {
    return this.open({ type: 'confirm', message, title, destructive, confirmText }).then(
      (v) => v === true
    );
  }

  prompt(message: string, title = 'Input', placeholder = ''): Promise<string | null> {
    return this.open({ type: 'prompt', message, title, placeholder }).then(
      (v) => (typeof v === 'string' ? v : null)
    );
  }

  /** Called by DialogComponent when the user clicks OK / Confirm */
  _resolve(value: string | boolean | null): void {
    const s = this.state();
    if (s.resolve) s.resolve(value);
    this.state.set({ ...s, open: false, resolve: null });
  }

  /** Called by DialogComponent when the user cancels */
  _cancel(): void {
    const s = this.state();
    if (s.resolve) s.resolve(s.type === 'confirm' ? false : null);
    this.state.set({ ...s, open: false, resolve: null });
  }
}
